import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import { AccessPlanType } from "__generated__/gql/graphql";

export enum BrandQuota {
  BrandRetailer = "brandRetailer",
  FormBotRun = "formBotRun",
  ReviewProgramSource = "reviewProgramSource",
  Sku = "sku",
}

/** Stable display order for plan quota summaries. */
export const BRAND_QUOTA_DISPLAY_ORDER = [
  BrandQuota.Sku,
  BrandQuota.BrandRetailer,
  BrandQuota.FormBotRun,
  BrandQuota.ReviewProgramSource,
] as const;

/** User-facing entity labels for each brand quota type. */
export const BRAND_QUOTA_ENTITY_LABELS: Record<
  BrandQuota,
  {
    plural: string;
    title: string;
  }
> = {
  [BrandQuota.BrandRetailer]: {
    plural: "retailers",
    title: "Retailers",
  },
  [BrandQuota.FormBotRun]: {
    plural: "filled form runs",
    title: "Filled form runs",
  },
  [BrandQuota.ReviewProgramSource]: {
    plural: "category review sources",
    title: "Category Review Sources",
  },
  [BrandQuota.Sku]: {
    plural: "SKUs",
    title: "SKUs",
  },
};

/** Per-quota plan limits from the current brand's access plan. Null means unlimited. */
export type QuotaLimitByQuota = Record<BrandQuota, number | null | undefined>;

const quotaLimitsAccessPlanFragment = gql(/* GraphQL */ `
  fragment QuotaLimitsAccessPlan on AccessPlan {
    id
    skuLimit
    brandRetailerLimit
    formBotRunLimit
    reviewProgramSourceLimit
  }
`);

export type QuotaLimitsAccessPlanFragment = FragmentType<
  typeof quotaLimitsAccessPlanFragment
>;

/** Maps access plan quota fields to the shared quota enum. A brand without an access plan is unlimited. */
export function getQuotaLimits(
  accessPlanFragmentRef: QuotaLimitsAccessPlanFragment | null | undefined,
): QuotaLimitByQuota {
  if (accessPlanFragmentRef === null) {
    return {
      [BrandQuota.BrandRetailer]: null,
      [BrandQuota.FormBotRun]: null,
      [BrandQuota.ReviewProgramSource]: null,
      [BrandQuota.Sku]: null,
    };
  }

  const accessPlan = getFragmentData(
    quotaLimitsAccessPlanFragment,
    accessPlanFragmentRef,
  );

  return {
    [BrandQuota.BrandRetailer]: accessPlan?.brandRetailerLimit,
    [BrandQuota.FormBotRun]: accessPlan?.formBotRunLimit,
    [BrandQuota.ReviewProgramSource]: accessPlan?.reviewProgramSourceLimit,
    [BrandQuota.Sku]: accessPlan?.skuLimit,
  };
}

/** Derived quota display and blocking state for one quota-backed record type. */
export type QuotaState = {
  /** True when a numeric limit and usage count are loaded and usage is at or above the limit. */
  isAtOrOverQuota: boolean;
  /** True when the current brand is on the free access plan. */
  isFreeTier: boolean;
  /** True when the access plan explicitly has no limit for this quota. */
  isUnlimited: boolean;
  /** Current plan limit. Null means unlimited; undefined means the limit has not loaded. */
  limit: number | null | undefined;
  /** Remaining records for limited quotas, or null when usage/limit is unavailable or unlimited. */
  remaining: number | null;
  /** Free plans show usage; paid plans only show when the quota is at or over the limit. */
  shouldShowBanner: boolean;
  /** Current usage count. Undefined means usage has not loaded. */
  used: number | null | undefined;
};

/** Builds quota state from a cached plan limit, volatile usage count, and access plan type. */
export function getQuotaState({
  limit,
  planType,
  used,
}: {
  limit: number | null | undefined;
  planType: AccessPlanType | null | undefined;
  used: number | null | undefined;
}): QuotaState {
  const isUnlimited = limit === null;

  const isFreeTier = planType === AccessPlanType.FREE;

  const isAtOrOverQuota =
    typeof limit === "number" && typeof used === "number" && used >= limit;

  const remaining =
    typeof limit === "number" && typeof used === "number"
      ? Math.max(limit - used, 0)
      : null;

  const shouldShowBanner =
    typeof limit === "number" &&
    typeof used === "number" &&
    (isFreeTier || isAtOrOverQuota);

  return {
    isAtOrOverQuota,
    isFreeTier,
    isUnlimited,
    limit,
    remaining,
    shouldShowBanner,
    used,
  };
}
